<template>
  <div class="filters-checkboxes">

    <header class="filters-checkboxes__header"
            @click="toggleOpen"
    >

      <h4 class="filters-checkboxes__header-title">
        {{ title }}
      </h4>

      <span :class="headerAmountClasses">
        {{ activeFiltersAmount }}
      </span>

      <button class="filters-checkboxes__header-button"
              @click.prevent
      >
        <InlineSvg src="@/assets/images/icons/chevron-down.svg"
                   :class="headerIconClasses"
                   />
      </button>

    </header>

    <div class="filters-checkboxes__container"
         :style="containerStyle"
    >
      <Checkbox v-model:checked="value"
                v-for="(option, key) in options"
                :disabled="!option.enabled"
                :key="key"
                :label="option.title"
                :value="option.id"
                :class="[
                  'filters-checkboxes__checkbox',
                  { 'filters-checkboxes__checkbox--disabled': !option.enabled },
                ]"
                />
    </div>

    <button :class="moreButtonClasses"
            @click.prevent="toggleShowAll"
    >

      <InlineSvg src="@/assets/images/icons/caret-down.svg"
                   class="filters-checkboxes__more-icon"
                   />

      Alle opties

    </button>

  </div>
</template>

<script>
import Checkbox from '@/components/UI/Checkbox.vue';
import InlineSvg from '@/components/UI/InlineSvg.vue';

const DEFAULT_VISIBLE_FILTERS_AMOUNT = 6;

export default {
  name: 'FiltersCheckboxes',

  components: {
    Checkbox,
    InlineSvg,
  },

  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    open: {
      type: Boolean,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    showAll: false,
  }),

  computed: {
    activeFiltersAmount() {
      return this.value.filter(this.isValidFilter).length;
    },

    containerStyle() {
      let visibleOptions = DEFAULT_VISIBLE_FILTERS_AMOUNT;

      if (!this.open) {
        visibleOptions = 0;
      } else if (!this.hasMaxOptions || this.showAll) {
        visibleOptions = this.optionsAmount;
      }

      const height = visibleOptions * 28;

      return {
        height: `${height}px`,
      };
    },

    hasMaxOptions() {
      return this.optionsAmount >= DEFAULT_VISIBLE_FILTERS_AMOUNT;
    },

    headerAmountClasses() {
      return [
        'filters-checkboxes__header-amount',
        { 'filters-checkboxes__header-amount--visible': this.activeFiltersAmount > 0 },
      ];
    },
    headerIconClasses() {
      return [
        'filters-checkboxes__header-icon',
        { 'filters-checkboxes__header-icon--open': this.open },
      ];
    },

    moreButtonClasses() {
      return [
        'filters-checkboxes__more-button',
        { 'filters-checkboxes__more-button--visible': this.open && !this.showAll && this.hasMaxOptions },
      ];
    },

    optionsAmount() {
      return Object.keys(this.options).length;
    },

    value: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
  },

  methods: {
    isValidFilter(filterKey) {
      return this.options.find((option) => option.id === filterKey) !== undefined;
    },

    toggleOpen() {
      this.$emit('updateOpenFilter', this.title);
    },

    toggleShowAll() {
      this.showAll = !this.showAll;
    },
  },
};
</script>

<style lang="scss" scoped>
  .filters-checkboxes {

    &:not(:last-child) {
      margin-bottom: var(--spacing-m);
    }

    &__header {
      display: flex;
      align-items: center;
      font-family: var(--font-secondary);
      cursor: pointer;

      &-title {
        margin: 0;
        font-size: 1em;
        text-transform: uppercase;
      }

      &-amount {
        display: flex;
        justify-content: center;
        align-items: center;
        min-width: 20px;
        height: 20px;
        padding: 0 .75ch;
        margin-left: .5ch;
        background-color: var(--color-primary);
        color: white;
        border-radius: 10px;
        font-size: 12px;
        line-height: 1;
        transition-property: transform, opacity;
        transition-duration: .2s;

        &:not(&--visible) {
          opacity: 0;
          transform: scaleY(0);
        }
      }

      &-button {
        padding: 10px;
        margin: 0 -10px 0 auto;
        outline: none;
        background-color: transparent;
      }

      &-icon {
        display: block;
        height: 1em;
        color: black;
        transition: transform .2s;

        &--open {
          transform: scaleY(-1);
        }
      }
    }

    &__container {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transition: height .4s;
    }

    &__checkbox {

      &--disabled {
        order: 1;
      }
    }

    &__more {

      &-button {
        display: flex;
        height: 26px;
        padding: 5px 0;
        outline: none;
        background-color: transparent;
        color: var(--color-primary);
        font-weight: 400;
        text-align: left;
        text-transform: none;
        transition-property: height, padding, opacity;
        transition-duration: .4s;

        &:not(&--visible) {
          height: 0;
          padding: 0;
          opacity: 0;
          pointer-events: none;
        }
      }

      &-icon {
        height: 1em;
        padding: 0 4px;
        margin-right: 10px;
      }
    }
  }
</style>
